/*
  This file is part of the project: "Digiziege"
  An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
  GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

  Copyright (c) 2026 Digiziege Team TH-Bingen

  Description:
  Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
  master data management and health tracking.
  Built as a PWA with a local-first approach to ensure data privacy.

  LICENSE INFORMATION:
  This program is free software: you can redistribute it and/or modify it under the terms of the
  GNU Affero General Public License as published by the Free Software Foundation,
  either version 3 of the License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
  without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
  See the GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License along with this program.
  If not, see <https://www.gnu.org/licenses/>.
*/

import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { famachaRepository } from '@/services/repositories/famacha.repository.js';

/**
 * FAMACHA Store
 * Manages FAMACHA check state and provides actions to interact with FAMACHA data
 */
export const useFamachaStore = defineStore('famacha', () => {
  // State
  const famachaChecks = ref([]);
  const loading = ref(false);
  const error = ref(null);

  // Computed/Getters
  const checkCount = computed(() => famachaChecks.value.length);

  /**
   * Get checks with critical scores (4 or 5 indicating severe anemia)
   */
  const criticalChecks = computed(() => 
    famachaChecks.value.filter(check => check.score >= 4)
  );

  /**
   * Get checks with concerning scores (3 or higher)
   */
  const concerningChecks = computed(() => 
    famachaChecks.value.filter(check => check.score >= 3)
  );

  /**
   * Get healthy checks (score 1-2)
   */
  const healthyChecks = computed(() => 
    famachaChecks.value.filter(check => check.score <= 2)
  );

  /**
   * Get the latest check (most recent)
   */
  const latestCheck = computed(() => 
    famachaChecks.value.length > 0 ? famachaChecks.value[0] : null
  );

  // Actions
  /**
   * Fetch all FAMACHA checks for a specific goat
   * @param {number} goatId - Goat ID
   * @returns {Promise<void>}
   */
  async function fetchFamachaChecks(goatId) {
    loading.value = true;
    error.value = null;
    
    try {
      const id = Number(goatId);
      if (isNaN(id)) throw new Error('Invalid Goat ID.');
      
      famachaChecks.value = await famachaRepository.findByGoat(id);
      console.log(`Fetched ${famachaChecks.value.length} FAMACHA checks for goat ${id}`);
    } catch (e) {
      error.value = e.message;
      console.error('Failed to fetch FAMACHA checks:', e);
      famachaChecks.value = [];
    } finally {
      loading.value = false;
    }
  }

  /**
   * Fetch all FAMACHA checks (for analytics/reports)
   * @returns {Promise<void>}
   */
  async function fetchAllFamachaChecks() {
    loading.value = true;
    error.value = null;
    
    try {
      famachaChecks.value = await famachaRepository.findAll();
      console.log(`Fetched ${famachaChecks.value.length} total FAMACHA checks`);
    } catch (e) {
      error.value = e.message;
      console.error('Failed to fetch all FAMACHA checks:', e);
      famachaChecks.value = [];
    } finally {
      loading.value = false;
    }
  }

  /**
   * Get a single FAMACHA check by ID
   * @param {number} id - Check ID
   * @returns {Promise<import('../models/famacha.model.js').FamachaCheck|null>}
   */
  async function getFamachaCheckById(id) {
    loading.value = true;
    error.value = null;
    
    try {
      const check = await famachaRepository.findById(id);
      console.log(`Fetched FAMACHA check ${id}`);
      return check;
    } catch (e) {
      error.value = e.message;
      console.error('Failed to fetch FAMACHA check:', e);
      return null;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Create a new FAMACHA check
   * @param {{goatId:number, score:number, measuredAt?:string}} payload
   * @returns {Promise<number|null>}
   */
  async function createFamachaCheck(payload) {
    loading.value = true;
    error.value = null;
    
    try {
      const id = await famachaRepository.create(payload);
      console.log(`Created FAMACHA check ${id}`);
      
      // Always refresh the list for this goat
      await fetchFamachaChecks(payload.goatId);
      
      return id;
    } catch (e) {
      error.value = e.message;
      console.error('Failed to create FAMACHA check:', e);
      return null;
    }
    // Note: loading state is managed by fetchFamachaChecks, no finally block needed
  }

  /**
   * Update an existing FAMACHA check
   * @param {number} id
   * @param {{score:number, measuredAt?:string}} payload
   * @returns {Promise<boolean>}
   */
  async function updateFamachaCheck(id, payload) {
    loading.value = true;
    error.value = null;
    
    try {
      await famachaRepository.update(id, payload);
      console.log(`Updated FAMACHA check ${id}`);
      
      // Refresh the list
      const check = famachaChecks.value.find(c => c.id === id);
      if (check) {
        await fetchFamachaChecks(check.goatId);
      }
      
      return true;
    } catch (e) {
      error.value = e.message;
      console.error('Failed to update FAMACHA check:', e);
      return false;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Delete a FAMACHA check
   * @param {number} id
   * @returns {Promise<boolean>}
   */
  async function deleteFamachaCheck(id) {
    loading.value = true;
    error.value = null;
    
    try {
      const check = famachaChecks.value.find(c => c.id === id);
      await famachaRepository.delete(id);
      console.log(`Deleted FAMACHA check ${id}`);
      
      // Refresh the list if we have the goatId
      if (check) {
        await fetchFamachaChecks(check.goatId);
      }
      
      return true;
    } catch (e) {
      error.value = e.message;
      console.error('Failed to delete FAMACHA check:', e);
      return false;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Get the latest FAMACHA check for a goat
   * @param {number} goatId
   * @returns {Promise<import('../models/famacha.model.js').FamachaCheck|null>}
   */
  async function getLatestCheck(goatId) {
    loading.value = true;
    error.value = null;
    
    try {
      const check = await famachaRepository.latest(goatId);
      return check;
    } catch (e) {
      error.value = e.message;
      console.error('Failed to fetch latest FAMACHA check:', e);
      return null;
    } finally {
      loading.value = false;
    }
  }

  return {
    // State
    famachaChecks,
    loading,
    error,
    
    // Getters
    checkCount,
    criticalChecks,
    concerningChecks,
    healthyChecks,
    latestCheck,
    
    // Actions
    fetchFamachaChecks,
    fetchAllFamachaChecks,
    getFamachaCheckById,
    createFamachaCheck,
    updateFamachaCheck,
    deleteFamachaCheck,
    getLatestCheck
  };
});
